import React, { useState, useEffect, useRef } from 'react';
import SparkButton from './SparkButton';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

interface MagicalPreviewDisplayProps {
  onOpenPreview: () => void;
  buttonText?: string;
  showBackMessage?: boolean;
}

const MagicalPreviewDisplay: React.FC<MagicalPreviewDisplayProps> = ({
  onOpenPreview,
  buttonText = "OPEN PREVIEW",
  showBackMessage = true
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [glow, setGlow] = useState({ x: 50, y: 50 });
  const ref = useRef<HTMLDivElement | null>(null);

  // One-time particle cloud generation
  useEffect(() => {
    const cloud: Particle[] = Array.from({ length: 60 }).map(() => ({
      id: Math.random(),
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 25 + 15,
      delay: Math.random() * 8,
    }));
    setParticles(cloud);
  }, []);

  // Pointer glow effect
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setGlow({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  const cssStyles = `
    .magical-container {
      position: relative;
      width: 100%;
      aspect-ratio: 9/16;
      border-radius: 24px;
      overflow: hidden;
      background: radial-gradient(circle at 50% 50%, #1a1a3e 0%, #0a0a1f 50%, #000511 100%);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      padding: 2rem 1.5rem;
      box-sizing: border-box;
      isolation: isolate;
    }

    .magical-particle {
      position: absolute;
      border-radius: 9999px;
      opacity: 0.7;
      animation: magicalFloat 4s ease-in-out infinite;
      pointer-events: none;
    }

    .magical-glow {
      position: absolute;
      inset: 0;
      pointer-events: none;
      opacity: 0.35;
      transition: all 0.3s ease-out;
    }

    .magical-ambient {
      position: absolute;
      inset: 0;
      pointer-events: none;
      opacity: 0.2;
      background: radial-gradient(ellipse at center, #3b82f6 0%, transparent 70%);
      mix-blend-mode: screen;
    }

    .magical-text-upper {
      text-align: center;
      font-size: 1.5rem;
      font-weight: 700;
      color: #ffffff;
      line-height: 1.3;
      max-width: 280px;
      text-shadow: 0 0 10px rgba(139, 92, 246, 0.8),
                   0 0 20px rgba(236, 72, 153, 0.6);
      animation: magicalGlow 3s ease-in-out infinite;
      z-index: 10;
      position: relative;
    }

    .magical-button-container {
      z-index: 10;
      position: relative;
    }

    .magical-text-lower {
      text-align: center;
      font-size: 0.95rem;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.85);
      line-height: 1.4;
      max-width: 240px;
      text-shadow: 0 0 8px rgba(139, 92, 246, 0.5);
      animation: magicalPulse 2.5s ease-in-out infinite;
      z-index: 10;
      position: relative;
      min-height: 2.5rem;
    }

    .magical-text-lower.hidden {
      opacity: 0;
      pointer-events: none;
    }

    @keyframes magicalFloat {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.7; }
      25% { transform: translate(-12px, -25px) scale(1.1); opacity: 0.9; }
      50% { transform: translate(15px, 12px) scale(0.9); opacity: 0.6; }
      75% { transform: translate(-8px, -15px) scale(1.05); opacity: 0.8; }
    }

    @keyframes magicalGlow {
      0%, 100% { 
        text-shadow: 0 0 10px rgba(139, 92, 246, 0.8),
                     0 0 20px rgba(236, 72, 153, 0.6);
      }
      50% { 
        text-shadow: 0 0 15px rgba(236, 72, 153, 1),
                     0 0 30px rgba(139, 92, 246, 0.9);
      }
    }

    @keyframes magicalPulse {
      0%, 100% { opacity: 0.85; }
      50% { opacity: 0.95; filter: blur(0.3px); }
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      .magical-container {
        padding: 1.5rem 1rem;
        border-radius: 16px;
      }
      
      .magical-text-upper {
        font-size: 1.25rem;
        max-width: 240px;
      }
      
      .magical-text-lower {
        font-size: 0.85rem;
        max-width: 200px;
      }
    }

    @media (max-width: 480px) {
      .magical-container {
        padding: 1.25rem 0.75rem;
      }
      
      .magical-text-upper {
        font-size: 1.1rem;
        max-width: 200px;
      }
      
      .magical-text-lower {
        font-size: 0.8rem;
        max-width: 180px;
      }
    }
  `;

  return (
    <>
      <style>{cssStyles}</style>
      <div
        ref={ref}
        onMouseMove={handleMove}
        className="magical-container"
      >
        {/* Interactive radial glow */}
        <div
          className="magical-glow"
          style={{
            background: `radial-gradient(circle at ${glow.x}% ${glow.y}%, #8b5cf6 0%, #3b82f6 30%, transparent 60%)`,
          }}
        />

        {/* Ambient magical glow */}
        <div className="magical-ambient" />

        {/* Particle cloud */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {particles.map((p) => (
            <div
              key={p.id}
              className="magical-particle"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: p.size,
                height: p.size,
                background: 'radial-gradient(circle, #8b5cf6 0%, #3b82f6 50%, #ec4899 100%)',
                boxShadow: '0 0 12px #3b82f6',
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Upper text */}
        <div className="magical-text-upper">
          This invitation is too magical to display here — see it in a new tab now
        </div>

        {/* Center button */}
        <div className="magical-button-container">
          <SparkButton
            variant="secondary"
            onClick={onOpenPreview}
            style={{
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.6)',
              transform: 'scale(1.1)',
            }}
          >
            {buttonText}
          </SparkButton>
        </div>

        {/* Lower text - always rendered but hidden when not needed */}
        <div className={`magical-text-lower ${!showBackMessage ? 'hidden' : ''}`}>
          Don't forget to come back to this tab after you watch it!
        </div>
      </div>
    </>
  );
};

export default MagicalPreviewDisplay;